import {JSX, useContext, useEffect, useState} from "react";
import { Grid, Typography, Stack, Box, Avatar, useTheme, useMediaQuery } from "@mui/material";
import { EventDto } from "../../interfaces/IEvent";
import { useNavigate, useParams } from "react-router-dom";
import { enqueueSnackbar } from "notistack";
import DetailsContainer from "./DetailsComponents/DetailsContainer";
import PostContainer from "./PostComponents/PostContainer";
import MapContainer from "./MapComponents/MapContainer";
import AffectedCustomersContainer from "./AffectedEntitiesComponents/AffectedEntitiesContainer";
import AlertContainer from "./AlertComponents/AlertContainer";
import HistoryContainer from "./HistoryComponent/HistoryContainer";
import { toDateTime } from "../../utils/ValueFormatters";
import ResponsiveDivider from "../../components/Divider/ResponsiveDivider";
import { EventType, EventTypeShort } from "../../interfaces/IEventEnums";
import ExternalCommunicationContainer from "./ExternalCommunicationComponents/ExternalCommunicationContainer";
import GridSettings from "./GridSettings/GridSettings";
import {  GridContainer, GridLayout } from "../../interfaces/IGridSettings";
import { useAppContext } from "../../context/AppSettingsContextProvider";
import { EbAuthenticatedAppContext } from "@eidsivabredband/app-framework";
import { CustomContainer } from "../../components/Wrapper/CustomContainer";
import IncidentContainers from "./ViewEventContainers/IncidentContainers";
import GenericContainers from "./ViewEventContainers/GenericContainers";
import PlannedWorkContainers from "./ViewEventContainers/PlannedWorkContainers";
import ChecklistContainer from "./ChecklistComponents/ChecklistContainer";
import RootBreadcrumb from "../../components/navigation/RootBreadcrumb";
import DocumentContainer from "./DocumentComponents/DocumentContainer";
import AssignmentContainer from "./AssignmentComponents/AssignmentContainer";

function Event() {
    const {context} = useContext(EbAuthenticatedAppContext);
    const [viewEvent, setViewEvent] = useState<EventDto>();
    const [gridLayout, setGridLayout] = useState<JSX.Element[]>([]);
    const [mdDownGridLayout, setMdDownGridLayout] = useState<JSX.Element[]>([]);
    const eventId = useParams();
    const navigate = useNavigate();
    const { eventGridLayout } = useAppContext();
    
    const typeEventGridLayout = () => {
        if(viewEvent?.type === EventType.Generell){
            return eventGridLayout.Generell
        }

        if(viewEvent?.type === EventType.Fellesfeil){
            return eventGridLayout.Fellesfeil
        }

        return eventGridLayout.Planlagt;
    }

    const theme = useTheme();
    const isMdDown = useMediaQuery(theme.breakpoints.down('md'));
    const isSmallDown = useMediaQuery(theme.breakpoints.down('sm'));

    const avatarText = () => {
        if(viewEvent?.type === EventType.Planlagt){
            return EventTypeShort.Planlagt;
        }
        if(viewEvent?.type === EventType.Fellesfeil){
            return EventTypeShort.Fellesfeil;
        }
        if(viewEvent?.type === EventType.Generell){
            return EventTypeShort.Generell;
        }
    }

    const avatarBgColor = () => {
        if(viewEvent?.type === EventType.Planlagt){
            return theme.palette.eventType.planlagt;
        }
        if(viewEvent?.type === EventType.Fellesfeil){
            return theme.palette.eventType.fellesfeil;
        }
        if(viewEvent?.type === EventType.Generell){
            return theme.palette.eventType.generell;
        }
    }

    const handleGetEvent = async (eventId: string | undefined) => {
        const response = await context.restApiClient
            .buildRequest(`/api/get-event/${eventId}`)
            .deserializeResponseToJson<EventDto>()
            .getAsync();

        if(response.json){
            setViewEvent(response.json);

            return response.json;
        }

        if(response.error){
            enqueueSnackbar(
                <Typography variant="body2">Kunne ikke hente hendelse. Prøv igjen eller kontakt systemansvarlig.</Typography>,
                { variant: "error" }
            );
            return navigate("/404");
        };
    };

    const getContainer = (container: GridContainer, index: number) => {
        if(!viewEvent) return (<Box key={index}></Box>);

        const containers = {
            [EventType.Generell]: <GenericContainers key={index} container={container} index={index} viewEvent={viewEvent} handleGetEvent={handleGetEvent} />,
            [EventType.Fellesfeil]: <IncidentContainers key={index} container={container} index={index} viewEvent={viewEvent} handleGetEvent={handleGetEvent} />,
            [EventType.Planlagt]: <PlannedWorkContainers key={index} container={container} index={index} viewEvent={viewEvent} handleGetEvent={handleGetEvent} />
        }

        return containers[viewEvent.type];
    }
    

    const handleSetGridLayout = (gridLayout: GridLayout) => {
        if(!gridLayout.columns) return;

        let buildGrid: JSX.Element[] = [];

        typeEventGridLayout().columns.forEach((column, index) => {
            column.container.length > 0 &&
                buildGrid.push(
                    <Grid
                        key={index}
                        size={{
                            sm: column.width,
                            xs: 12
                        }}>
                        <Grid container spacing={2} sx={{
                            justifyContent: column.align
                        }}>
                            {column.container.map((container, index) => {
                                return getContainer(container, index)}
                            )}
                        </Grid>
                    </Grid>
                );
        });

        setGridLayout(buildGrid);
    };

    useEffect(() => {
        if(!viewEvent) return;

        setMdDownGridLayout([
            <Grid
                key={1}
                size={{
                    sm: 12,
                    xs: 12
                }}>
                <Stack spacing={2}>
                    <DetailsContainer
                        viewEvent={viewEvent}
                        onUpdateEventView={handleGetEvent}
                    />
                    <DocumentContainer
                        viewEvent={viewEvent}
                    />
                    {viewEvent.type === EventType.Planlagt &&
                        <ChecklistContainer
                            viewEvent={viewEvent}
                            onUpdateEventView={handleGetEvent}
                        />
                    }
                    <AssignmentContainer
                        viewEvent={viewEvent}
                        onUpdateEventView={handleGetEvent}
                    />
                </Stack>
            </Grid>,
            <Grid
                key={2}
                size={{
                    sm: 12,
                    xs: 12
                }}>
                <Stack spacing={2}>
                    <AffectedCustomersContainer
                        viewEvent={viewEvent}
                    />
                    <AlertContainer 
                        viewEvent={viewEvent} 
                        onUpdateEventView={handleGetEvent}
                    />
                    {viewEvent.type !== EventType.Generell &&
                        <MapContainer 
                            viewEvent={viewEvent} 
                        />
                    }
                </Stack>
            </Grid>,
            <Grid
                key={3}
                size={{
                    sm: 12,
                    xs: 12
                }}>
                <Stack spacing={2}>
                    <PostContainer
                        eventId={viewEvent.id}
                        eventPosts={viewEvent.eventPosts}
                        onUpdateEventView={handleGetEvent}
                    />
                </Stack>
            </Grid>
        ]);

        handleSetGridLayout(typeEventGridLayout());
    }, [viewEvent]);

    useEffect(() => {
        handleGetEvent(eventId.id);
    }, [context.restApiClient]);

    return (
        <>
            {viewEvent && viewEvent.id && (
                <CustomContainer gridWidth={typeEventGridLayout().width}>
                    <Stack direction={"row"} spacing={"auto"} sx={{
                        alignItems: "center"
                    }}>
                        <RootBreadcrumb>
                            <Typography sx={{ textTransform: "capitalize" }}>
                                {viewEvent.id}
                            </Typography>
                        </RootBreadcrumb>
                        
                        {isMdDown &&
                            <Box sx={{
                                mt: 2
                            }}>
                                <ExternalCommunicationContainer
                                    viewEvent={viewEvent}
                                    onUpdateEventView={handleGetEvent}
                                />
                            </Box>
                        }
                    </Stack>
                    <Stack
                        direction={"row"}
                        spacing={1}
                        sx={{
                            marginTop: 4,
                            alignItems: isMdDown ? "flex-start" : "center"
                        }}>
                        <Stack direction={"column"} spacing={1}>
                            <Avatar 
                                sx={{
                                    height: isMdDown ? "40px" : "50px",
                                    width: isMdDown ? "40px" : "50px",
                                    bgcolor: avatarBgColor(),
                                }}
                            >
                                {avatarText()}
                            </Avatar>
                        </Stack>
                            <Stack direction={isSmallDown ? "column" : "row"} sx={{
                                width: "100%"
                            }}>
                                <Box>
                                    <Typography variant="h5" sx={{
                                        alignSelf: "center"
                                    }}>
                                        {viewEvent.eventDetails.title}
                                    </Typography>
                                    <Typography
                                        sx={{
                                            color: "text.disabled",
                                            fontStyle: "italic",
                                            fontSize: "15px"
                                        }}>
                                        Opprettet av {viewEvent.createdBy.name} - {toDateTime(new Date(viewEvent.createdOn as Date))}
                                    </Typography>
                                </Box>
                                {!isMdDown &&
                                    <Box
                                        style={{ 
                                            marginLeft: "auto",
                                            marginTop: undefined,
                                        }}
                                        sx={{
                                            alignItems: "center",
                                            display: "flex"
                                        }}>
                                        <ExternalCommunicationContainer
                                            viewEvent={viewEvent}
                                            onUpdateEventView={handleGetEvent}
                                        />
                                    </Box>
                                }
                            </Stack>
                    </Stack>
                    <Box sx={{
                        paddingBottom: 1
                    }}>
                        <ResponsiveDivider />
                    </Box>
                    <Grid container spacing={2} sx={{
                        justifyContent: typeEventGridLayout().align
                    }}>
                        {isMdDown ? mdDownGridLayout : gridLayout}
                    </Grid>
                    <HistoryContainer viewEvent={viewEvent} />
                    {!isMdDown && (
                        <GridSettings
                            viewEvent={viewEvent}
                            onChange={handleSetGridLayout}
                        />
                    )}
                </CustomContainer>
            )}
        </>
    );
}

export default Event;
